import { PaperclipIcon, UploadCloudIcon } from 'lucide-react';
import { useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { FileRef } from '@/shared/api/types';
import { useFormatters } from '@/shared/i18n/format';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';

import { AttachmentList } from './AttachmentList';
import { AttachmentSourceButtons } from './AttachmentSources';
import { UploadTaskList } from './UploadTaskList';
import { useFileUploads, type FileUploadsState } from './useFileUploads';

export interface FileUploaderProps {
  /** Uploaded files (FileRef from `POST /api/files`). */
  value: readonly FileRef[];
  onChange: (files: FileRef[]) => void;
  multiple?: boolean;
  /** `accept` attribute, e.g. `image/*,.pdf`. */
  accept?: string;
  /** Per-file limit in bytes (default and maximum: 200 MB). */
  maxSize?: number;
  maxFiles?: number;
  /** `dropzone` – large drop area; `button` – compact "Attach" button. */
  variant?: 'dropzone' | 'button';
  /** Hide the list of uploaded files (when the parent renders AttachmentList itself). */
  hideList?: boolean;
  disabled?: boolean;
  /** Notifies the parent while uploads are running (e.g. to disable "Send"). */
  onUploadingChange?: (uploading: boolean) => void;
  buttonLabel?: ReactNode;
  /** Offer the registered attachment sources ("Choose from Disk"); default true. */
  sources?: boolean;
  className?: string;
}

/**
 * Hidden file input + trigger for a `useFileUploads` state (used by FileUploader and composers), followed by the
 * registered attachment sources ("Choose from Disk") as icon buttons.
 */
export function FileUploadButton({
  uploads,
  multiple = true,
  accept,
  disabled,
  label,
  sources = true,
  className,
}: {
  uploads: FileUploadsState;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  label?: ReactNode;
  /** Offer the registered attachment sources next to the button (default true). */
  sources?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('shared');
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <div
        {...uploads.dragProps}
        className={cn('inline-flex rounded-md', uploads.dragging && 'ring-2 ring-brand', className)}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <PaperclipIcon />
          {label ?? t('files.attach')}
        </Button>
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          tabIndex={-1}
          multiple={multiple}
          accept={accept}
          disabled={disabled}
          onChange={uploads.onInputChange}
        />
      </div>
      {sources && <AttachmentSourceButtons uploads={uploads} compact disabled={disabled} />}
    </>
  );
}

/**
 * Drag & drop / click uploader for the Files API with per-file progress, cancel and retry.
 * Controlled: `value` holds the uploaded FileRefs; in-flight uploads are internal state.
 */
export function FileUploader({
  value,
  onChange,
  multiple = true,
  accept,
  maxSize,
  maxFiles,
  variant = 'dropzone',
  hideList = false,
  disabled = false,
  onUploadingChange,
  buttonLabel,
  sources = true,
  className,
}: FileUploaderProps) {
  const { t } = useTranslation('shared');
  const fmt = useFormatters();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploads = useFileUploads({
    value,
    onChange,
    multiple,
    accept,
    maxSize,
    maxFiles,
    disabled,
    onUploadingChange,
  });
  const browse = () => inputRef.current?.click();

  return (
    <div className={cn('space-y-2', className)} data-slot="file-uploader">
      {variant === 'dropzone' ? (
        <div
          {...uploads.dragProps}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          aria-label={t('files.dropzoneLabel')}
          onClick={browse}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              browse();
            }
          }}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border-strong bg-muted/30 px-6 py-7 text-center focus-ring transition-colors hover:border-brand/50 hover:bg-brand-soft/40',
            uploads.dragging && 'border-brand bg-brand-soft/60',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <span className="flex size-10 items-center justify-center rounded-full bg-background text-brand-text shadow-xs ring-1 ring-border">
            <UploadCloudIcon className="size-5" />
          </span>
          <span className="text-sm">
            <span className="font-medium text-brand-text">{t('files.browse')}</span> {t('files.orDrop')}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('files.maxSize', { max: fmt.fileSize(uploads.maxSize) })}
          </span>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            className="sr-only"
            tabIndex={-1}
            multiple={multiple}
            accept={accept}
            disabled={disabled}
            onChange={uploads.onInputChange}
          />
        </div>
      ) : (
        <FileUploadButton
          uploads={uploads}
          multiple={multiple}
          accept={accept}
          disabled={disabled}
          label={buttonLabel}
          sources={sources}
        />
      )}
      {variant === 'dropzone' && sources && (
        <AttachmentSourceButtons uploads={uploads} disabled={disabled} className="flex justify-center" />
      )}
      <UploadTaskList uploads={uploads} />
      {!hideList && value.length > 0 && (
        <AttachmentList
          files={value}
          onRemove={disabled ? undefined : (file) => onChange(value.filter((f) => f.id !== file.id))}
        />
      )}
    </div>
  );
}
